import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { WowFormController } from "ui-common";
import { SignalElement, inject } from "ui-common/di";
import type { IAuthState } from "ui-common/services/auth/types";
import type { IRpcService } from "ui-common/services/rpc/types";
import "ui-components/modal/wow-modal";
import { t, translate } from "../../translations/i18n";

// Form data interface for form fields only
interface HandleFormData {
  handle: string;
}

@customElement("change-handle-modal")
export class ChangeHandleModal extends SignalElement {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @state()
  private isFormSubmitting = false;

  private formController!: WowFormController<HandleFormData>;

  override connectedCallback() {
    super.connectedCallback();
    this.initializeFormController();
  }

  private initializeFormController() {
    this.formController = new WowFormController<HandleFormData>(
      this,
      { handle: this.authState.currentUser.get()?.handle || "" },
      {
        validateOnBlur: true,
        validators: {
          handle: [this.validateHandle.bind(this)],
        },
      },
      (event) => this.handleFormSubmit(event),
    );
  }

  private async validateHandle(value: string) {
    if (this.authState.currentUser.get()?.handle === value) {
      return "";
    }

    if (!value) {
      return translate("onboarding.handleValidationLength");
    }

    if (value.length < 3) {
      return translate("onboarding.handleValidationLength");
    }

    if (!/^[a-z0-9_\-.]+$/.test(value)) {
      return translate("onboarding.handleValidationPattern");
    }

    try {
      const { available } = await this.rpcService.call<{ available: boolean }>("checkHandle", {
        handle: value.toLowerCase(),
      });

      if (!available) {
        return translate("onboarding.handleUnavailable");
      }

      return "";
    } catch (error) {
      console.error("Handle validation error:", error);
      return translate("onboarding.handleUnavailable");
    }
  }

  private handleFormSubmit(event: { data?: HandleFormData; isValid: boolean }) {
    if (!event.isValid || !event.data) return;

    this.isFormSubmitting = true;

    this.dispatchEvent(
      new CustomEvent("handle-changed", {
        detail: { newHandle: event.data.handle },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private get saveButtonText() {
    if (this.isFormSubmitting) return t("discounts.modal.saving");
    return t("editWorld.saveButton");
  }

  private handleModalClose = () => {
    this.dispatchEvent(
      new CustomEvent("modal-close", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  private async handleChangeHandle() {
    // Trigger form submission through form controller
    await this.formController.submit();
  }

  protected override render() {
    return html`<wow-modal
      variant="overlay"
      @close=${this.handleModalClose}
      @footer-button-clicked=${this.handleChangeHandle}
    >
      <span slot="header">Change your handle</span>
      <span slot="footer-button-text">${this.saveButtonText}</span>

      <div class="modal-content">
        <form class="handle-form">
          <div class="form-layout">
            <wow-input name=${this.formController.fieldNames.handle} label="Handle" required></wow-input>
          </div>
        </form>
      </div>
    </wow-modal>`;
  }
}
